import client from "./client";

// ────────── Enums ──────────

export type ReviewCycleStatus = "DRAFT" | "ACTIVE" | "CALIBRATION" | "CLOSED";

export type ReviewType = "SELF" | "MANAGER" | "PEER" | "UPWARD";

export type ReviewStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "SUBMITTED"
  | "ACKNOWLEDGED";

export type GoalCategory = "INDIVIDUAL" | "TEAM" | "COMPANY";

export type GoalStatus =
  | "DRAFT"
  | "ACTIVE"
  | "ON_TRACK"
  | "AT_RISK"
  | "COMPLETED"
  | "CANCELLED";

// ────────── Entity Types ──────────

export interface ReviewCycle {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  status: ReviewCycleStatus;
  starts_at: string;
  ends_at: string;
  launched_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewCycleCreate {
  name: string;
  description?: string | null;
  starts_at: string;
  ends_at: string;
}

export interface Review {
  id: string;
  org_id: string;
  cycle_id: string;
  subject_employee_id: string;
  reviewer_employee_id: string;
  review_type: ReviewType;
  status: ReviewStatus;
  overall_rating: number | null;
  strengths: string | null;
  growth_areas: string | null;
  comments: string | null;
  submitted_at: string | null;
  acknowledged_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewUpdate {
  overall_rating?: number | null;
  strengths?: string | null;
  growth_areas?: string | null;
  comments?: string | null;
}

export interface Goal {
  id: string;
  org_id: string;
  owner_employee_id: string;
  cycle_id: string | null;
  title: string;
  description: string | null;
  category: GoalCategory;
  status: GoalStatus;
  progress: number;
  target_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalCreate {
  owner_employee_id?: string;
  cycle_id?: string | null;
  title: string;
  description?: string | null;
  category?: GoalCategory;
  status?: GoalStatus;
  progress?: number;
  target_date?: string | null;
}

export type OneOnOneMood = "great" | "good" | "okay" | "struggling";
export type OneOnOneStatus = "scheduled" | "completed" | "cancelled";

export interface ActionItem {
  text: string;
  completed: boolean;
  due_date?: string | null;
  assignee_employee_id?: string | null;
}

export interface TalkingPoint {
  text: string;
  added_by_employee_id?: string | null;
}

export interface OneOnOne {
  id: string;
  org_id: string;
  manager_employee_id: string;
  report_employee_id: string;
  scheduled_at: string;
  agenda: string | null;
  shared_notes: string | null;
  private_manager_notes: string | null;
  status: OneOnOneStatus | string;
  action_items: ActionItem[];
  talking_points: TalkingPoint[];
  mood: OneOnOneMood | string | null;
  follow_up_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OneOnOneCreate {
  manager_employee_id: string;
  report_employee_id: string;
  scheduled_at: string;
  agenda?: string | null;
  shared_notes?: string | null;
  private_manager_notes?: string | null;
  action_items?: ActionItem[];
  talking_points?: TalkingPoint[];
  mood?: OneOnOneMood | string | null;
  follow_up_date?: string | null;
}

export interface OneOnOneUpdate {
  scheduled_at?: string;
  agenda?: string | null;
  shared_notes?: string | null;
  private_manager_notes?: string | null;
  action_items?: ActionItem[];
  talking_points?: TalkingPoint[];
  mood?: OneOnOneMood | string | null;
  follow_up_date?: string | null;
  status?: OneOnOneStatus | string;
}

export interface ActionItemWithContext {
  one_on_one_id: string;
  manager_employee_id: string;
  report_employee_id: string;
  scheduled_at: string;
  index: number;
  item: ActionItem;
}

// ────────── Review Cycles ──────────

export async function listReviewCycles(): Promise<ReviewCycle[]> {
  const { data } = await client.get<ReviewCycle[]>("/performance/cycles");
  return data;
}

export async function createReviewCycle(
  payload: ReviewCycleCreate,
): Promise<ReviewCycle> {
  const { data } = await client.post<ReviewCycle>(
    "/performance/cycles",
    payload,
  );
  return data;
}

export async function getReviewCycle(id: string): Promise<ReviewCycle> {
  const { data } = await client.get<ReviewCycle>(
    `/performance/cycles/${id}`,
  );
  return data;
}

export async function updateReviewCycle(
  id: string,
  payload: Partial<ReviewCycleCreate>,
): Promise<ReviewCycle> {
  const { data } = await client.patch<ReviewCycle>(
    `/performance/cycles/${id}`,
    payload,
  );
  return data;
}

export async function launchReviewCycle(id: string): Promise<ReviewCycle> {
  const { data } = await client.post<ReviewCycle>(
    `/performance/cycles/${id}/launch`,
  );
  return data;
}

export async function closeReviewCycle(id: string): Promise<ReviewCycle> {
  const { data } = await client.post<ReviewCycle>(
    `/performance/cycles/${id}/close`,
  );
  return data;
}

// ────────── Reviews ──────────

export async function listReviews(params?: {
  cycle_id?: string;
  subject?: "me" | string;
  reviewer?: "me" | string;
}): Promise<Review[]> {
  const { data } = await client.get<Review[]>("/performance/reviews", {
    params,
  });
  return data;
}

export async function getReview(id: string): Promise<Review> {
  const { data } = await client.get<Review>(`/performance/reviews/${id}`);
  return data;
}

export async function updateReview(
  id: string,
  payload: ReviewUpdate,
): Promise<Review> {
  const { data } = await client.patch<Review>(
    `/performance/reviews/${id}`,
    payload,
  );
  return data;
}

export async function submitReview(id: string): Promise<Review> {
  const { data } = await client.post<Review>(
    `/performance/reviews/${id}/submit`,
  );
  return data;
}

export async function acknowledgeReview(id: string): Promise<Review> {
  const { data } = await client.post<Review>(
    `/performance/reviews/${id}/acknowledge`,
  );
  return data;
}

// ────────── Goals ──────────

export async function listGoals(params?: {
  owner?: "me" | string;
  cycle_id?: string;
  category?: GoalCategory;
  status?: GoalStatus;
}): Promise<Goal[]> {
  const { data } = await client.get<Goal[]>("/performance/goals", {
    params,
  });
  return data;
}

export async function createGoal(payload: GoalCreate): Promise<Goal> {
  const { data } = await client.post<Goal>("/performance/goals", payload);
  return data;
}

export async function getGoal(id: string): Promise<Goal> {
  const { data } = await client.get<Goal>(`/performance/goals/${id}`);
  return data;
}

export async function updateGoal(
  id: string,
  payload: Partial<GoalCreate>,
): Promise<Goal> {
  const { data } = await client.patch<Goal>(
    `/performance/goals/${id}`,
    payload,
  );
  return data;
}

export async function deleteGoal(id: string): Promise<void> {
  await client.delete(`/performance/goals/${id}`);
}

// ────────── 1:1s ──────────

export async function listOneOnOnes(params?: {
  manager?: "me" | string;
  report?: "me" | string;
}): Promise<OneOnOne[]> {
  const { data } = await client.get<OneOnOne[]>("/performance/one-on-ones", {
    params,
  });
  return data;
}

export async function createOneOnOne(
  payload: OneOnOneCreate,
): Promise<OneOnOne> {
  const { data } = await client.post<OneOnOne>(
    "/performance/one-on-ones",
    payload,
  );
  return data;
}

export async function getOneOnOne(id: string): Promise<OneOnOne> {
  const { data } = await client.get<OneOnOne>(
    `/performance/one-on-ones/${id}`,
  );
  return data;
}

export async function updateOneOnOne(
  id: string,
  payload: OneOnOneUpdate,
): Promise<OneOnOne> {
  const { data } = await client.patch<OneOnOne>(
    `/performance/one-on-ones/${id}`,
    payload,
  );
  return data;
}

export async function completeOneOnOne(id: string): Promise<OneOnOne> {
  const { data } = await client.post<OneOnOne>(
    `/performance/one-on-ones/${id}/complete`,
  );
  return data;
}

export async function listOneOnOnesForEmployee(
  employeeId: string,
): Promise<OneOnOne[]> {
  const { data } = await client.get<OneOnOne[]>(
    `/performance/one-on-ones/employee/${employeeId}`,
  );
  return data;
}

export async function listOpenActionItems(): Promise<ActionItemWithContext[]> {
  const { data } = await client.get<ActionItemWithContext[]>(
    "/performance/one-on-ones/action-items",
  );
  return data;
}
